import { useSyncExternalStore } from 'react';
import { MIN_AGE, MIN_BROADCAST_AGE, TERMS_VERSION, type AgeGroup, type TermsAgreement } from '@zip2live/shared/safety';
import { ApiError } from './api.ts';
import { ensureSession } from './auth.ts';
import { refreshMe } from './me.ts';

/**
 * ZIP2LIVE's rules and the person's age, asked once before the first go-live (here, or an OBS key)
 * and the first chat message, guests included, as the phone app does. 13 and over to use ZIP2LIVE,
 * 18 and over to go live. The agreement is kept on the server (it follows a guest into their account)
 * and in this browser, so the dialog shows once. The server refuses a go-live or a chat message
 * without it (403 terms_required / age_required). The dialog is components/Rules.tsx.
 */
export type RulesFor = 'live' | 'chat';

const AGREED_KEY = 'zip2live_terms';
/** Someone who said they're under 13 isn't asked again in this browser for a day. */
const TOO_YOUNG_KEY = 'zip2live_too_young_until';
const TOO_YOUNG_MS = 24 * 3600_000;

function saved(): TermsAgreement | null {
  try {
    const raw = localStorage.getItem(AGREED_KEY);
    return raw ? (JSON.parse(raw) as TermsAgreement) : null;
  } catch {
    return null;
  }
}

export async function currentAgreement(): Promise<TermsAgreement | null> {
  const me = await refreshMe();
  return me?.user?.terms ?? saved();
}

/** Agreed to today's rules, and old enough for this. */
export const enough = (a: TermsAgreement | null, purpose: RulesFor) => Boolean(a && a.version === TERMS_VERSION && (purpose === 'chat' || a.ageGroup === 'adult'));

export function tooYoung(): boolean {
  try {
    return Number(localStorage.getItem(TOO_YOUNG_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

export function markTooYoung() {
  try {
    localStorage.setItem(TOO_YOUNG_KEY, String(Date.now() + TOO_YOUNG_MS));
  } catch {
    /* asked again next time */
  }
}

/** The age group for someone this old (null: under 13, who can't use ZIP2LIVE). */
export const ageGroupFor = (age: number): AgeGroup | null => (age < MIN_AGE ? null : age < MIN_BROADCAST_AGE ? 'teen' : 'adult');

export async function agree(ageGroup: AgeGroup): Promise<TermsAgreement> {
  await ensureSession();
  const res = await fetch('/api/v1/me/terms', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version: TERMS_VERSION, ageGroup, ageSource: 'self', source: 'web' }),
  });
  const body = (await res.json().catch(() => null)) as { terms?: TermsAgreement; error?: { code: string; message: string } } | null;
  if (!res.ok || !body?.terms) throw new ApiError(res.status, body?.error?.code ?? 'error', body?.error?.message ?? 'Something went wrong. Try again.');
  try {
    localStorage.setItem(AGREED_KEY, JSON.stringify(body.terms));
  } catch {
    /* the server has it */
  }
  void refreshMe();
  return body.terms;
}

/** Signed out or deleted, or the server says this account hasn't agreed: ask again next time. */
export function forgetAgreement() {
  try {
    localStorage.removeItem(AGREED_KEY);
  } catch {
    /* nothing stored */
  }
}

// ---- The open request, shown by <RulesDialog /> ----

let request: { id: number; purpose: RulesFor; resolve: (ok: boolean) => void } | null = null;
let seq = 0;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

export function useRulesRequest() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => void listeners.delete(cb);
    },
    () => request,
  );
}

/**
 * Resolves true once the person has agreed (and is old enough for `purpose`), after the rules dialog
 * if needed; false if they close it.
 */
export async function ensureAgreed(purpose: RulesFor): Promise<boolean> {
  if (!tooYoung() && enough(await currentAgreement().catch(() => saved()), purpose)) return true;
  return new Promise((resolve) => {
    request?.resolve(false);
    request = { id: ++seq, purpose, resolve };
    changed();
  });
}

/** The dialog is done, whichever way. */
export function finishRules(ok: boolean) {
  const done = request;
  request = null;
  changed();
  done?.resolve(ok);
}

/** A refusal for want of the rules (or of age): the next attempt asks again. */
export function isRulesError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return code === 'terms_required' || code === 'age_required';
}
